import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, ClipboardList, CheckCircle2, AlertCircle, Send, Plus, Trash2 } from "lucide-react";
import { UniHeader } from "./_lmsShared";

export default function UniversityAssignment() {
  const { assignmentId } = useParams();
  const [params] = useSearchParams();
  const enrollmentId = params.get("enrollment_id") || "";
  const courseCode = params.get("course_code") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [newAttUrl, setNewAttUrl] = useState("");
  const [newAttLabel, setNewAttLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await universityApi.assignmentGet(assignmentId, enrollmentId);
      setData(r.data);
      if (r.data?.my_submission?.submission_text) {
        setText(r.data.my_submission.submission_text);
        setAttachments(r.data.my_submission.attachments || []);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Could not load assignment"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [assignmentId, enrollmentId]);

  const wordCount = useMemo(
    () => (text.trim() ? text.trim().split(/\s+/).length : 0),
    [text]
  );

  const a = data?.assignment;
  const sub = data?.my_submission;
  const isGradedPass = sub?.status === "graded" && (sub?.score || 0) >= (a?.passing_score || 80);
  const isGradedFail = sub?.status === "graded" && (sub?.score || 0) < (a?.passing_score || 80);
  const isPending = sub?.status === "submitted";
  const wcMin = a?.word_count_min || 0;

  const onSubmit = async () => {
    if (!text.trim()) return toast.error("Please write a response before submitting.");
    if (wcMin > 0 && wordCount < wcMin) {
      return toast.error(`Response must be at least ${wcMin} words (currently ${wordCount}).`);
    }
    setSubmitting(true);
    try {
      await universityApi.assignmentSubmit(assignmentId, enrollmentId, {
        submission_text: text.trim(),
        attachments,
      });
      toast.success("Assignment submitted for grading.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const addAttachment = () => {
    if (!newAttUrl.trim()) return;
    setAttachments((s) => [...s, { label: newAttLabel.trim() || newAttUrl, url: newAttUrl.trim() }]);
    setNewAttLabel("");
    setNewAttUrl("");
  };
  const removeAttachment = (i) =>
    setAttachments((s) => s.filter((_, idx) => idx !== i));

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Assignment" back={courseCode ? `/university/courses/${enrollmentId}/${courseCode}` : "/university/student"} />
      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="uni-assignment-page">
        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : !a ? (
          <div className="text-slate-500">Assignment not found.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
              <ClipboardList className="w-3.5 h-3.5" />Assignment · <span className="font-mono">{a.course_code}</span>
            </div>
            <h1 className="font-display text-3xl text-slate-900" data-testid="uni-assignment-title">{a.title}</h1>
            {a.description && <p className="mt-2 text-sm text-slate-700">{a.description}</p>}

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Tile label="Passing score" value={`${a.passing_score || 80}%`} />
              <Tile label="Required" value={a.required ? "Yes" : "No"} />
              <Tile label="Min words" value={wcMin > 0 ? wcMin : "—"} />
              <Tile label="Due" value={a.due_at ? new Date(a.due_at).toLocaleDateString() : "—"} />
            </div>

            {a.instructions && (
              <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Instructions</div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{a.instructions}</p>
              </section>
            )}

            {/* Rubric preview */}
            {Array.isArray(a.rubric_criteria) && a.rubric_criteria.length > 0 && (
              <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Grading Rubric</div>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  {a.rubric_criteria.map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-[11px] text-purple-900 w-12 shrink-0">{c.points || 0}pt</span>
                      <div>
                        <div className="text-slate-900">{c.name}</div>
                        {c.description && <div className="text-[12px] text-slate-500">{c.description}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Status panel — graded */}
            {sub && (
              <section
                className={`mt-6 rounded-2xl border p-5 ${isGradedPass ? "border-emerald-200 bg-emerald-50" : isGradedFail ? "border-rose-200 bg-rose-50" : "border-purple-200 bg-purple-50"}`}
                data-testid="uni-assignment-status"
                data-status={sub.status}
                data-score={sub.score || 0}
              >
                <div className="flex items-center gap-2">
                  {isGradedPass ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-purple-900" />}
                  <span className="text-sm font-medium text-slate-900">
                    {isGradedPass ? `Passed — ${sub.score}%` : isGradedFail ? `Not passed — ${sub.score}% (need ${a.passing_score || 80}%)` : isPending ? "Submitted · awaiting grade" : sub.status}
                  </span>
                </div>
                {sub.instructor_comments && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Instructor comments</div>
                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{sub.instructor_comments}</p>
                  </div>
                )}
                {Array.isArray(sub.rubric_scores) && sub.rubric_scores.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Rubric breakdown</div>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {sub.rubric_scores.map((r, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="font-mono text-[11px] text-slate-700 w-20 shrink-0">{r.points || 0}/{r.max_points || 0}</span>
                          <div>
                            <span className="text-slate-900">{r.name}</span>
                            {r.comment && <div className="text-[12px] text-slate-500">{r.comment}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {/* Submission form (allow re-submit unless graded as passing) */}
            {!isGradedPass && (
              <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Your response</div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={12}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono"
                  placeholder="Write your response here. Markdown is fine."
                  data-testid="uni-assignment-text"
                />
                <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                  <span>
                    Word count: <span className="font-mono text-slate-900">{wordCount}</span>
                    {wcMin > 0 && (
                      <span className={`ml-2 ${wordCount >= wcMin ? "text-emerald-700" : "text-rose-700"}`}>
                        (min {wcMin})
                      </span>
                    )}
                  </span>
                  <span>Auto-saved to draft when you click Submit.</span>
                </div>

                {/* Attachments (URL list) */}
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Attachments (URLs)</div>
                  {attachments.length > 0 && (
                    <ul className="text-sm text-slate-700 mb-2 space-y-1">
                      {attachments.map((att, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <a href={att.url} target="_blank" rel="noreferrer" className="text-purple-900 hover:underline truncate">{att.label || att.url}</a>
                          <button onClick={() => removeAttachment(i)} className="text-rose-600 hover:underline" data-testid={`uni-att-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <input value={newAttLabel} onChange={(e) => setNewAttLabel(e.target.value)} placeholder="Label (optional)" className="md:col-span-4 rounded-xl border border-slate-200 px-3 h-9 text-sm" />
                    <input value={newAttUrl} onChange={(e) => setNewAttUrl(e.target.value)} placeholder="https://…" className="md:col-span-7 rounded-xl border border-slate-200 px-3 h-9 text-sm" data-testid="uni-att-url" />
                    <button onClick={addAttachment} className="md:col-span-1 rounded-xl border border-slate-200 text-slate-700 h-9 text-sm inline-flex items-center justify-center" data-testid="uni-att-add"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                  <Link to={`/university/courses/${enrollmentId}/${courseCode}`} className="text-xs text-slate-500 hover:underline">Cancel</Link>
                  <button
                    onClick={onSubmit}
                    disabled={submitting}
                    className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="uni-assignment-submit"
                  >
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {sub ? "Resubmit" : "Submit for grading"}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 font-medium">{value}</div>
    </div>
  );
}
